import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Camera, Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logoIcon from '../images/logoicon.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPortfolioOpen, setIsPortfolioOpen] = useState(false);
  const location = useLocation();

  const portfolioCategories = [
    { path: '/portfolio/bridal', label: 'Bridal Collection' },
    { path: '/portfolio/ready-to-wear', label: 'Ready to Wear' },
    { path: '/portfolio/sustainable', label: 'Sustainable Fashion' },
    { path: '/portfolio/custom', label: 'Custom Design' },
  ];

  const navItems = [
    { path: '/', label: 'Home' },
    {
      path: '/portfolio',
      label: 'Portfolio',
      hasDropdown: true,
      onMouseEnter: () => setIsPortfolioOpen(true),
      onMouseLeave: () => setIsPortfolioOpen(false),
    },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/blog', label: 'Blog' },
    { path: '/testimonials', label: 'Testimonials' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="fixed w-full z-50 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <motion.div
              whileHover={{ scale: 1.1, rotate: 5 }}
              whileTap={{ scale: 0.95 }}
              animate={{ 
                rotate: [0, 5, 0],
                scale: [1, 1.05, 1]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="w-10 h-10 rounded-full overflow-hidden"
            >
              <img src={logoIcon} alt="AES Logo" className="w-full h-full object-cover" />
            </motion.div>
            <span className="text-lg font-bold">Advance Elegance Studio</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <div
                key={item.path}
                className="relative"
                onMouseEnter={item.onMouseEnter}
                onMouseLeave={item.onMouseLeave}
              >
                <Link
                  to={item.path}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors inline-flex items-center gap-1 ${
                    isActive(item.path)
                      ? 'text-emerald-400'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {item.label}
                  {item.hasDropdown && (
                    <motion.div
                      animate={{ rotate: isPortfolioOpen ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </motion.div>
                  )}
                </Link>

                {/* Portfolio Dropdown */}
                {item.hasDropdown && (
                  <AnimatePresence>
                    {isPortfolioOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute left-0 mt-1 w-56 bg-black/95 backdrop-blur-sm border border-gray-800 rounded-lg overflow-hidden"
                      >
                        {portfolioCategories.map((category) => (
                          <Link
                            key={category.path}
                            to={category.path}
                            className="block px-4 py-2 text-sm text-gray-300 hover:bg-emerald-400/10 hover:text-emerald-400 transition-colors"
                          >
                            {category.label}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-gray-400 hover:text-white transition-colors"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-4">
            {navItems.map((item) => (
              <React.Fragment key={item.path}>
                <Link
                  to={item.path}
                  onClick={() => {
                    setIsOpen(false);
                    if (item.hasDropdown) {
                      setIsPortfolioOpen(!isPortfolioOpen);
                    }
                  }}
                  className={`block px-4 py-2 rounded-lg text-sm transition-colors ${
                    isActive(item.path)
                      ? 'text-emerald-400'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    {item.label}
                    {item.hasDropdown && (
                      <ChevronDown
                        className={`h-4 w-4 transform transition-transform ${
                          isPortfolioOpen ? 'rotate-180' : ''
                        }`}
                      />
                    )}
                  </div>
                </Link>
                {item.hasDropdown && isPortfolioOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="pl-6"
                  >
                    {portfolioCategories.map((category) => (
                      <Link
                        key={category.path}
                        to={category.path}
                        onClick={() => setIsOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-300 hover:text-emerald-400 transition-colors"
                      >
                        {category.label}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;